import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import InputAdornment from "@mui/material/InputAdornment";
import Box from "@mui/material/Box";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import debounce from "lodash/debounce";
import isFunction from "lodash/isFunction";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";

const autocomplete_ = (theme) => ({
  width: 400,
  [theme.breakpoints.down("sm")]: {
    width: "70vw",
    marginRight: "30px",
  },
});

const optionGrid_ = (theme) => ({
  borderBottom: "1px solid #e9e9e9",
  [theme.breakpoints.down("sm")]: {
    // For small screen sizes, counteract the 16px padding-left pushed on us
    // by the <li> styling.
    marginLeft: "-16px",
  },
});

const paper_ = (theme) => ({
  boxShadow: "none",
  overflow: "hidden",
});

const listbox_ = (theme) => ({
  padding: 0,
  maxHeight: "80vh",
  [theme.breakpoints.down("sm")]: {
    maxHeight: "87vh", // more room since the search-type buttons go away
  },
});

// wrapper for autocomplete where suggestions are kept open and acts like a dropdown/filter
export default function AutocompleteMenu(params) {
  // @params: input display, defaultdata, input label
  // @advanceParams: input change handler, render option fn
  // @inheritedParams: open, selecthandler

  const {
    open, // open autocomplete result
    selectHandler, // user clicked an option
    optionDisplay, // {selected, label, labelId, labelContext}
    inputLabel, // label in inputfield
    options = [], // options to be shown
    onInputChange, // inputfield value
    alwaysShowContext,
    loading = false,
    frontEndFilter = false, // true for searches that do not use API
  } = params;

  const { selected } = optionDisplay;

  const theme = useTheme();
  const xsDown = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const isValid = !!options && Array.isArray(options) && options.length > 0;
  const autocompleteOptions = isValid ? options : [];

  // useEffect(() => {
  //   const isValid = !!options && Array.isArray(options) && options.length > 0;
  //   console.log("_______");
  //   console.log("debug in AutocompleteMenu");
  //   console.log("options:", options);
  //   console.log("options isValid:", isValid);
  //   console.log("setAutocompleteOptions input:", isValid ? options : []);
  // }, [options]);

  const setInputChangeHandler = debounce((value) => {
    if (onInputChange) {
      onInputChange(value);
    }
  }, 500);

  const inputChangeHandler = (event) => {
    setInputChangeHandler(event.target.value);
  };

  const matchesInputValue = (option, selected, inputValue) => {
    return (
      option[selected]
        .toLowerCase()
        .indexOf(inputValue.trim().toLowerCase()) !== -1
    );
  };

  const filterHandler = (options, state) => {
    const { inputValue } = state;
    // if front end filtered, filter by selected values
    if (frontEndFilter) {
      const filteredOptions = options.filter((o) => {
        let returnValue;
        if (Array.isArray(selected)) {
          returnValue = selected.some((selection) => {
            return matchesInputValue(o, selection, inputValue);
          });
        } else {
          returnValue = matchesInputValue(o, selected, inputValue);
        }
        return returnValue;
      });
      // else return all API response
      return filteredOptions;
    }
    return options;
  };

  return (
    <Autocomplete
      open={open}
      loading={loading}
      sx={autocomplete_}
      onChange={(event, newValue) => {
        if (typeof newValue === "string") {
          // Can get in here if you type input but have not selected an option
          // (either with key down + enter or mouse click) and press ENTER
          // In this case, newValue is a string and not an option object.
          // If available, we will select the first filtered option object by default.
          // (The filtered option list itself is not available at this component level,
          // so we have to re-use the filter function.)
          const filteredOptions = filterHandler(autocompleteOptions, {
            inputValue: newValue,
          });
          if (filteredOptions.length > 0) {
            selectHandler(filteredOptions[0]);
          }
        } else {
          selectHandler(newValue);
        }
      }}
      freeSolo
      autoHighlight
      disableListWrap
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          // Can get in here if you type input that yields zero results
          // and then press ENTER.  In this case, option is a string
          // (because of freeSolo mode) and we cannot safely do the
          // below code.
          return option;
        }
        if (Array.isArray(selected)) {
          let arrString = "";
          selected.forEach((s) => {
            arrString = arrString.concat(option[s] + " ");
          });
          return arrString;
        }
        return option[selected];
      }}
      options={autocompleteOptions}
      filterOptions={filterHandler}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={true}
          variant={xsDown ? "standard" : "outlined"}
          label={inputLabel}
          onChange={inputChangeHandler}
          fullWidth
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps?.input,
              endAdornment: (
                <>
                  {loading ? (
                    <InputAdornment position="end">
                      <CircularProgress color="inherit" size={20} />
                    </InputAdornment>
                  ) : null}
                  {params.slotProps?.input?.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option, { inputValue }) => {
        const { key, ...optionProps } = props;
        return (
          <li key={key} {...optionProps} style={{ paddingBottom: 0 }}>
            <Option
              option={option}
              inputValue={inputValue}
              optionDisplay={optionDisplay}
              xsDown={xsDown}
              alwaysShowContext={alwaysShowContext}
            />
          </li>
        );
      }}
      slotProps={{
        listbox: {
          sx: listbox_,
        },
        paper: {
          sx: paper_,
        },
      }}
    />
  );
}

export function Option({
  option,
  inputValue,
  optionDisplay,
  xsDown,
  alwaysShowContext,
}) {
  const { label, label2, label2Tooltip, labelId, labelContext } = optionDisplay;

  const labelStr = !isFunction(label) ? option[label] : label(option);
  const label2Str = !isFunction(label2) ? option[label2] : label2(option);
  const labelIdStr = !isFunction(labelId) ? option[labelId] : labelId(option);
  const contextStr = !isFunction(labelContext)
    ? option[labelContext]
    : labelContext(option);

  return (
    <>
      <Grid container sx={optionGrid_}>
        {!xsDown && <Grid size={{ xs: 1 }}></Grid>}
        <Grid container size={{ xs: 11 }} sx={{ paddingBottom: "5px" }}>
          <Grid
            size={{ xs: 12 }}
            sx={{ display: "flex", alignItems: "center", width: "100%" }}
          >
            <OptionLabel label={labelStr} inputValue={inputValue} />
            <OptionLabelId labelId={labelIdStr} inputValue={inputValue} />
            {xsDown && alwaysShowContext && (
              <span style={{ marginLeft: "4px" }}>
                <OptionContext context={contextStr} inputValue={inputValue} />
              </span>
            )}
            {label2Str && (
              <Box
                sx={{
                  marginLeft: "auto",
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: "4px",
                }}
              >
                <OptionLabel label={label2Str} inputValue={inputValue} />
                {!xsDown && label2Tooltip && (
                  <Tooltip title={label2Tooltip}>
                    <IconButton size="small" sx={{ p: 0.5, ml: 0.5 }}>
                      <LocalHospitalIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Grid>{" "}
          {!xsDown && (
            <Grid size={{ xs: 12 }}>
              <OptionContext context={contextStr} inputValue={inputValue} />
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
}

function OptionLabel({ label, inputValue }) {
  const { text, style } = !isObj(label) ? { text: label, style: {} } : label;
  const labelMatches = match(text, inputValue);
  const labelParts = parse(text, labelMatches);

  return (
    <span data-testid="search-option-label" style={{ whiteSpace: "nowrap" }}>
      {labelParts.map((part, index) => (
        <span
          key={index}
          style={{
            fontSize: "0.875rem",
            fontWeight: part.highlight ? 700 : 400,
            ...style,
            marginLeft: index ? 0 : style.marginLeft || 0,
          }}
        >
          {part.text}
        </span>
      ))}
    </span>
  );
}

function OptionLabelId({ labelId, inputValue }) {
  const { text, style } = !isObj(labelId)
    ? { text: labelId, style: {} }
    : labelId;
  const optionIdMatches = match(text, inputValue);
  const optionIdParts = parse(text, optionIdMatches);

  return (
    <span data-testid="search-option-id">
      <span style={{ marginLeft: "4px" }}>
        (
        {optionIdParts.map((part, index) => (
          <span
            key={index}
            style={{
              fontSize: "0.875rem",
              fontWeight: part.highlight ? 700 : 400,
              ...style,
              marginLeft: index ? 0 : style.marginLeft || 0,
            }}
          >
            {part.text}
          </span>
        ))}
        )
      </span>
    </span>
  );
}

function OptionContext({ context, inputValue }) {
  const { text, style } = !isObj(context)
    ? { text: context, style: {} }
    : context;
  const optionContextMatches = match(text, inputValue);
  const optionContextParts = parse(text, optionContextMatches);

  return (
    <span data-testid="search-option-context">
      {optionContextParts.map((part, i) => (
        <span
          key={i}
          style={{
            color: "rgba(0, 0, 0, 0.55)",
            fontSize: "0.7rem",
            whiteSpace: "nowrap",
            fontWeight: part.highlight ? 700 : 400,
            ...style,
            marginLeft: i ? 0 : style.marginLeft || 0,
          }}
        >
          {part.text}
        </span>
      ))}
    </span>
  );
}

function isObj(target) {
  return typeof target === "object" && target !== null;
}
